import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '@/lib/api-client';
import { header, type Supplier } from './api';

export type SupplierState =
  | { status: 'loading' }
  | { status: 'not-found' }
  | { status: 'error' }
  | { status: 'ready'; supplier: Supplier };

/** `GET /v1/expenses/suppliers/{id}`, with a reload and a way to take the supplier a write returned. */
export function useSupplier(supplierId: string) {
  const [state, setState] = useState<SupplierState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);

  useEffect(() => {
    let active = true;
    setState({ status: 'loading' });
    apiClient
      .GET('/v1/expenses/suppliers/{supplierId}', {
        params: {
          path: { supplierId },
          header: header(),
        },
      })
      .then(({ data, error, response }) => {
        if (!active) return;
        if (response.status === 404) {
          setState({ status: 'not-found' });
          return;
        }
        if (error !== undefined || !response.ok || data === undefined) {
          setState({ status: 'error' });
          return;
        }
        setState({ status: 'ready', supplier: data });
      })
      .catch(() => {
        if (active) setState({ status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [supplierId, reloads]);

  const reload = useCallback(() => setReloads((count) => count + 1), []);
  const replace = useCallback((supplier: Supplier) => setState({ status: 'ready', supplier }), []);

  return { state, reload, replace } as const;
}
